/**
 * CODE INTENT: Renders device SMS controls (lock/unlock, recovery mode, beep, directions).
 * IN: deviceDetails, devicesList, optional map center + recovery mode state.
 * CHANGE remove: active CSS class on Lock/Unlock icons — blue background hid blue padlock glyphs (same regression as prior attempt).
 */
import React, { useEffect, useRef, useState } from "react";

import activeImage from "../../assets/images/active-check.png";
import Speaker from "../../assets/images/Speaker.png";
import Recoverymode from "../../assets/images/recoverymode.png";
import Recovered from "../../assets/images/recovered.png";
import Map_Marker from "../../assets/images/Map_Marker.png";
import Lock from "../../assets/images/Lock.png";
import Unlock from "../../assets/images/Unlock.png";
import useApi from "../../hook/useAPI";
import { DEVICE, SMS_COMMANDS } from "../../utils/const";
import { findDeviceInList } from "../../utils/deviceRagDisplay";
import { toast } from "react-toastify";
import "./DeviceControlBar.scss";

const LOCK_STATE = {
  LOCKED: 'locked',
  UNLOCKED: 'unlocked',
};

const resolveLockStateFromDevice = (device) => {
  if (device?.lock_status === LOCK_STATE.LOCKED || device?.lock_status === LOCK_STATE.UNLOCKED) {
    return device.lock_status;
  }
  return null;
};

function DeviceControlBar({ deviceDetails, setCenter, devicesList, currentMode: externalCurrentMode, setCurrentMode: externalSetCurrentMode }) {
  const { postData } = useApi();
  const [DevicesList, setDevicesList] = useState(devicesList);
  // Use external currentMode if provided, otherwise use local state
  const [internalCurrentMode, internalSetCurrentMode] = useState(SMS_COMMANDS.RECOVER_MODE_30);
  const currentMode = externalCurrentMode !== undefined ? externalCurrentMode : internalCurrentMode;
  const setCurrentMode = externalSetCurrentMode !== undefined ? externalSetCurrentMode : internalSetCurrentMode;
  const [lockState, setLockState] = useState(null);
  const selectedDeviceIdRef = useRef(null);
  const simCardNumber = deviceDetails?.simCardNumber;

  const isLocked = lockState === LOCK_STATE.LOCKED;
  const isUnlocked = lockState === LOCK_STATE.UNLOCKED;

  const sendSMS = async (msg) => {
    const oncetoken = localStorage.getItem("1nce_access_token");
    let updateMode = false;
    let updateLockState = null;
    let data = {
      simId: simCardNumber,
      token: oncetoken,
      message: msg,
      device_id: deviceDetails?._id
    };

    let url = DEVICE.SEND_SMS;
    if (msg === SMS_COMMANDS.RECOVER_MODE_3 || msg === SMS_COMMANDS.RECOVER_MODE_30) {
      updateMode = true;
    } else if (msg === SMS_COMMANDS.LOCK) {
      updateLockState = LOCK_STATE.LOCKED;
    } else if (msg === SMS_COMMANDS.UNLOCK) {
      updateLockState = LOCK_STATE.UNLOCKED;
    }

    await postData(url, data).then((res) => {
      toast.success(res?.message);
      if (updateMode) {
        setCurrentMode(msg)
      }
      const fromResponse = resolveLockStateFromDevice({ lock_status: res?.lock_status });
      if (fromResponse) {
        setLockState(fromResponse);
      } else if (updateLockState) {
        setLockState(updateLockState);
      }
    }).catch((err) => {
      toast.error(err?.response?.data?.error);
    })
  };

  useEffect(() => {
    if (!deviceDetails) {
      return;
    }
    const deviceId = String(deviceDetails._id);
    const deviceChanged = selectedDeviceIdRef.current !== deviceId;
    selectedDeviceIdRef.current = deviceId;

    const locationRow = findDeviceInList(deviceDetails, devicesList);
    const resolved =
      resolveLockStateFromDevice(deviceDetails) ??
      resolveLockStateFromDevice(locationRow);

    setLockState((prev) => {
      if (resolved) {
        return resolved;
      }
      return deviceChanged ? null : prev;
    });

    if (deviceDetails.recovery_mode === 'rc_3' || deviceDetails.recovery_mode === undefined) {
      setCurrentMode(SMS_COMMANDS.RECOVER_MODE_3)
    } else {
      setCurrentMode(SMS_COMMANDS.RECOVER_MODE_30)
    }
  }, [deviceDetails, devicesList, setCurrentMode]);

  useEffect(() => {
    // console.log(DevicesList, "DevicesList")
    if (!!DevicesList && DevicesList.length > 0) {
      const deviceLocation = DevicesList?.filter((data) => data?.deviceId === deviceDetails?._id);
      const lat = deviceLocation[0]?.latestRecord?.latitude
      const lng = deviceLocation[0]?.latestRecord?.longitude

      localStorage.setItem("current_select_device_id", deviceDetails?._id);
      if (!!lat && !!lng && setCenter) {
        setCenter({ lat: Number(lat), lng: Number(lng) })
      } else if (deviceLocation.length > 0 && deviceLocation[0].latitude && deviceLocation[0].longitude && setCenter) {
        setCenter({ lat: Number(deviceLocation[0].latitude), lng: Number(deviceLocation[0].longitude) })

      } else if (setCenter) {
        setCenter({ lat: 33.5779, lng: -101.8552 })
      }
    }
  }, [DevicesList])

  const redirectToDirection = () => {
    const deviceLocation = devicesList?.filter((data) => data?.deviceId === deviceDetails?._id);
    console.log(devicesList, "deviceLocation")
    const lat = deviceLocation[0]?.latitude

    const lng = deviceLocation[0]?.longitude
    console.log(lat, lng, "lat lng")

    const googleMapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}&travelmode=driving`;
    window.open(googleMapsUrl, "_blank")
  }

  if (!deviceDetails) {
    return null;
  }

  return (
    <>
      <div className="device-control-bar">
        <div className="row g-4">
          <div className="col-6 cpl-sm-6 col-md-4 mb-4">
            <div onClick={() => sendSMS(SMS_COMMANDS.UNLOCK)} className="device-control-items">
              <div className="device-control-items-icon">
                <img src={Unlock} alt="Unlock" />
                {isUnlocked && <img src={activeImage} className="active-image" alt="active" />}
              </div>
              <div className="device-control-items-name">Unlock</div>
              <div className="device-control-items-subtitle">{isUnlocked ? 'Unlocked' : 'Off'}</div>
            </div>
          </div>

          <div className="col-6 cpl-sm-6 col-md-4 mb-4">
            <div onClick={() => sendSMS(SMS_COMMANDS.LOCK)} className="device-control-items">
              <div className="device-control-items-icon">
                <img src={Lock} alt="Lock" />
                {isLocked && <img src={activeImage} className="active-image" alt="active" />}
              </div>
              <div className="device-control-items-name">Lock</div>
              <div className="device-control-items-subtitle">{isLocked ? 'Locked' : 'Off'}</div>
            </div>
          </div>

          <div className="col-6 cpl-sm-6 col-md-4 mb-4">
            <div onClick={() => redirectToDirection()} className="device-control-items">
              <div className="device-control-items-icon active">
                <img src={Map_Marker} alt="ChargedBattery" />
              </div>
              <div className="device-control-items-name">Directions</div>
              <div className="device-control-items-subtitle">Google Maps</div>
            </div>
          </div>


          <div className="col-6 cpl-sm-6 col-md-4 mb-4">
            <div onClick={() => sendSMS(SMS_COMMANDS.RECOVER_MODE_3)} className="device-control-items">
              <div className="device-control-items-icon">
                <img src={Recoverymode} alt="ChargedBattery" />
                {(currentMode === SMS_COMMANDS.RECOVER_MODE_3) && <img src={activeImage} className="active-image" alt="active" />}
              </div>
              <div className="device-control-items-name">Recovery Mode</div>
              <div className="device-control-items-subtitle">Track Every 3 Mins</div>
            </div>
          </div>
          <div className="col-6 cpl-sm-6 col-md-4 mb-4">
            <div onClick={() => sendSMS(SMS_COMMANDS.RECOVER_MODE_30)} className="device-control-items">
              <div className="device-control-items-icon">
                <img src={Recovered} alt="Recovered" />
                {(currentMode === SMS_COMMANDS.RECOVER_MODE_30) && <img src={activeImage} className="active-image" alt="active" />}
              </div>
              <div className="device-control-items-name">Recovered</div>
              <div className="device-control-items-subtitle">Track Every 30 Mins</div>
            </div>
          </div>

          <div className="col-6 cpl-sm-6 col-md-4 mb-4">
            <div onClick={() => sendSMS(SMS_COMMANDS.BEEP)} className="device-control-items">
              <div className="device-control-items-icon">
                <img src={Speaker} alt="ChargedBattery" />
              </div>
              <div className="device-control-items-name">Beep</div>
              <div className="device-control-items-subtitle">On</div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default DeviceControlBar;
